import TC from "@/components/table/Table.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { emptyRecordsText } from "@/components/empty.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { mcn } from "@/utils/baseProps.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ComponentProps, ReactNode } from "react";
import React, { Children, useState } from "react";

export interface WTableProps extends ComponentProps<"table"> {
    children: ReactNode;
    afterTable?: ReactNode;
    search?: ReactNode;
}

export interface WSmartTableProps<T> extends Omit<WTableProps, "children"> {
    /** `undefined` means loading */
    items: T[] | undefined;
    children: (item: T, index: number) => ReactNode;

    head?: ReactNode;
    hasViewAllButton?: boolean;
}

/** Responsive `<table>` wrapper. */
export function WTable({ children, afterTable, search, ...props }: WTableProps) {
    return (
        <div className="VStack gap-3">
            {search && <div className="px-2">{search}</div>}

            <div className={TC.tableContainer}>
                <table {...mcn(TC.data, props)}>{children}</table>
            </div>

            {Children.count(afterTable) > 0 && <div className="flex gap-0.5rem">{afterTable}</div>}
        </div>
    );
}

/**
 * `<tbody>` wrapper. Automatically displays an {@link EmptyRow} if there are no children.
 */
export function WTableBody({ children, ref, ...props }: { children: ReactNode } & ComponentProps<"tbody">) {
    // NB: Can not use `React.Children.count(children)` because it will count `false` as a child.
    const hasChildren = React.Children.toArray(children).length > 0;
    return (
        <tbody ref={ref} {...props}>
            {hasChildren ? children : <EmptyRow />}
        </tbody>
    );
}

/** Muted full-width row, with the "No records" text by default */
export function EmptyRow({ colSpan = 99, children }: { colSpan?: number; children?: ReactNode }) {
    return (
        <tr className={TC.empty}>
            <td colSpan={colSpan}>{children ?? emptyRecordsText}</td>
        </tr>
    );
}

export const WTableLoader = () => (
    <EmptyRow>
        <Loading icon="none" fillSpace />
    </EmptyRow>
);

export function WSmartTable<T>({
    children,
    items,

    head,
    afterTable,
    hasViewAllButton,
    ...tableProps
}: WSmartTableProps<T>) {
    const { t } = useLingui();
    const [viewAll, setViewAll] = useState<boolean>(!hasViewAllButton);

    function toggleViewAll() {
        setViewAll(!viewAll);
    }

    const isLoading = items === undefined;
    items ??= [];

    const limit = 10;
    const shownItems = viewAll ? items : items.slice(0, limit);
    const buttonVisible = hasViewAllButton && items.length > limit;

    return (
        <WTable
            {...tableProps}
            afterTable={
                (Children.count(afterTable) > 0 || buttonVisible) && (
                    <>
                        {buttonVisible && (
                            <WButton color="primary" size="bar" variant="border" action={toggleViewAll}>
                                {viewAll ? t`View last ${limit} records` : <Trans>View all records</Trans>}
                            </WButton>
                        )}

                        {afterTable}
                    </>
                )
            }
        >
            {head}
            <tbody>
                {isLoading ? (
                    <WTableLoader />
                ) : shownItems.length > 0 ? (
                    shownItems.map((item, index) => children(item, index))
                ) : (
                    <EmptyRow />
                )}
            </tbody>
        </WTable>
    );
}
